var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect, useState } from "react";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery } from "react-query";
import { checkAssignFeasibility } from "../api/productNumberService";
import { getAgencyLabelMessageId } from "../utils/getAgencyLabelMessageId";
/**
 * Derives everything the create dialog shows from a feasibility answer.
 *
 * Kept pure (and exported) so the permissive-defaults case can be tested directly: while the answer is
 * missing, every flag falls back to "not blocked / no prefix / no block size", which is only safe as long
 * as submitting is blocked too — otherwise the product is created without a number.
 */
export var buildFeasibilityView = function (_a) {
    var _b, _c;
    var feasibility = _a.feasibility, assignNumber = _a.assignNumber, isError = _a.isError, selectedPrefixId = _a.selectedPrefixId;
    var prefixOptions = (_b = feasibility === null || feasibility === void 0 ? void 0 : feasibility.prefixes) !== null && _b !== void 0 ? _b : [];
    return {
        assignBlocked: assignNumber && (feasibility === null || feasibility === void 0 ? void 0 : feasibility.canAssign) === false,
        showBlockSize: assignNumber &&
            (feasibility === null || feasibility === void 0 ? void 0 : feasibility.canAssign) === true &&
            feasibility.needsNewPrefix &&
            !feasibility.newPrefixSizeAutomatic,
        prefixOptions: prefixOptions,
        // Effective chosen prefix (defaults to the first when the user hasn't picked).
        effectivePrefixId: selectedPrefixId || ((_c = prefixOptions[0]) === null || _c === void 0 ? void 0 : _c.publisherPrefixId) || "",
        // No answer yet means no magnitude and no prefix id would be sent; a failed request stays submittable
        // so a broken feasibility endpoint cannot lock the dialog.
        submitBlocked: assignNumber && !feasibility && !isError,
    };
};
/**
 * Up-front feasibility of assigning a number for a title's publisher, so the user sees the reason
 * (exhausted prefix, closed publisher, ...) as soon as assignment is ticked — before creating the product.
 *
 * The primary content type decides the registration agency (musical notation = ISMN, anything else = ISBN),
 * so the answer has to follow the value the user picks in the form — otherwise the dialog would offer an
 * ISBN prefix for sheet music and the number would be drawn from the wrong agency.
 */
export var useAssignFeasibility = function (_a) {
    var titleId = _a.titleId, assignNumber = _a.assignNumber, primaryContentTypeValue = _a.primaryContentTypeValue;
    var ky = useOkapiKy();
    // Chosen publisher prefix when the publisher has more than one; empty = auto.
    var _b = useState(""), selectedPrefixId = _b[0], setSelectedPrefixId = _b[1];
    var _c = useQuery(["assignFeasibility", titleId, primaryContentTypeValue], function () { return checkAssignFeasibility(ky, titleId, primaryContentTypeValue); }, { enabled: !!titleId && assignNumber, cacheTime: 0 }), feasibility = _c.data, isError = _c.isError;
    // A prefix picked for the previous agency must never be submitted for the new one.
    useEffect(function () {
        setSelectedPrefixId("");
    }, [primaryContentTypeValue, setSelectedPrefixId]);
    return __assign({ feasibility: feasibility, selectedPrefixId: selectedPrefixId, setSelectedPrefixId: setSelectedPrefixId, agencyMessageId: getAgencyLabelMessageId(feasibility === null || feasibility === void 0 ? void 0 : feasibility.agency) }, buildFeasibilityView({
        feasibility: feasibility,
        assignNumber: assignNumber,
        isError: isError,
        selectedPrefixId: selectedPrefixId,
    }));
};
